import { Component, NgZone, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import moment from 'moment';
import { ApiClientConstant, ApiConnector, RequestQueryPayload, Table } from '@cureskin/api-client';
import { AppConfig } from '../../app.config';
import { ConnectionService } from '../../../services/connection-service';
import { ClipboardService } from '../../../services/copyToClipboard-service';
import { WindowRefService } from '../../../services/window-ref-service';
import { Broadcaster } from '../../../components/broadcaster';
import { AddPaymentModal } from './add-payment';
import { FaviconService } from '../../../services/favicon-service';
import { LocalStorage } from '../../../services/local-storage-service';

@Component({
    selector: 'order-view',
    templateUrl: './view.html',
    styleUrls: ['./view.scss'],
    standalone: false
})
export class ViewComponent implements OnDestroy {
  orderObj: any;
  note: string = '';
  regimenId: any = '';
  payments: Array<any>;
  subscriptions: Array<Subscription>;
  orderHistory: Array<any>;
  invoices: Array<any>;
  alternateNumber: any;
  mobileNumber: any;
  regimen: any;
  products: Array<any> = [];
  services: any;
  prescriptions: Array<any> = [];
  totalQuantity: number = 0;
  @ViewChild('orderView', { static: false }) orderView: ElementRef;
  @ViewChild('toast', { static: false }) toast: ElementRef;
  message: any;
  ui: { modal: { payment: { open: boolean } } };
  unpaidOrders: Array<any> = [];
  transferPaymentAllowed: boolean;
  skuIds: Array<string> = [];
  orderMedicalRecord: any = {};
  sopFieldsToShow: Array<string> = [];
  sopEditingDoctor: any;
  addedProducts: Array<string> = [];
  removedProducts: Array<string> = [];
  convertedBy: Table.User;
  currentUser: Table.User;
  showConvertedByButton: boolean;
  showUserMobileNumber: boolean = false;
  hasVariant4999Regimen: boolean = false;
  orderEditLogs: Array<any> = [];
  editedByName: string = '';
  canInitiateRefund: boolean = false;
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    public appConfig: AppConfig,
    private conn: ConnectionService,
    private zone: NgZone,
    private clipBoardService: ClipboardService,
    private window: WindowRefService,
    private broadcaster: Broadcaster,
    private dialog: MatDialog,
    private faviconService: FaviconService,
    private storage: LocalStorage) { }

  async ngOnInit(): Promise<void> {
    this.payments = [];
    this.subscriptions = [];
    this.orderHistory = [];
    this.invoices = [];
    this.ui = { modal: { payment: { open: false } } };
    this.orderObj = new Table.Order();
    await this.subscribeToUrlQueryParams();
    this.onlyProductsInOrder();
    await this.fetchSKUs();
    this.showConvertedByButton = await this.checkConvertedByButtonEligibility();
    this.currentUser = this.conn.getCurrentUser();
  }

  updateRefundEligibility(): void {
    const userRoles: Array<string> = this.storage.getJsonValue('userRoles') || [];
    const latestConsultationLog: Table.OrderConsultationLog = this.orderHistory[0];
    this.canInitiateRefund = userRoles.includes('paymentEditor')
      && latestConsultationLog?.get('stage') === ApiClientConstant.Order.Stage.REFUND_REQUESTED;
  }

  async updateOrderHistory(): Promise<void> {
    try {
      this.orderHistory = await this.conn.getOrderLog(this.orderObj);
    } catch (error) {
      this.broadcaster.broadcast('NOTIFY', { message: `Failed To fetch order history ${error.message}`, type: this.appConfig.Shared.Toast.Type.ERROR })
    }
  }

  updateTotalQuantity(): void {
    this.orderObj.get('productInfo')?.forEach((product: any) => this.totalQuantity += product.quantity || 1);
    this.orderObj.get('serviceInfo')?.forEach((service: any) => this.totalQuantity += service.quantity || 1);
    this.orderObj.get('couponInfo')?.forEach((coupon: any) => this.totalQuantity += coupon.quantity || 1);
  }

  async fetchSopQuestions(): Promise<void> {
    if (this.orderObj.get('type') !== ApiClientConstant.Order.Type.REGIMEN) return;
    const orderMedicalRecord = await ApiConnector.findOne(Table.OrderMedicalRecord, {
      where: { order: this.orderObj },
      include: ['doctor', 'order'],
    });
    if (!orderMedicalRecord) {
      this.orderMedicalRecord = undefined;
      return;
    }
    this.orderMedicalRecord = orderMedicalRecord.toJSON();
    delete this.orderMedicalRecord.ACL;
    delete this.orderMedicalRecord.createdAt;
    delete this.orderMedicalRecord.updatedAt;
    this.sopEditingDoctor = this.orderMedicalRecord.doctor.DoctorDisplayName;
    delete this.orderMedicalRecord.doctor;
    delete this.orderMedicalRecord.order;
    delete this.orderMedicalRecord.objectId;
    this.sopFieldsToShow = Object.keys(this.orderMedicalRecord);
    const sopRegimen: Table.Regimen['attributes'] = JSON.parse(JSON.stringify(await ApiConnector.findOne(Table.Regimen, {
      where: {
        regimenId: this.orderMedicalRecord.selectTreeRegimenOption,
        type: ApiClientConstant.Regimen.Type.MAIN,
      },
      include: ['products', 'variants.products' as 'variants'],
      project: ['regimenId', 'products.title' as 'products', 'variants.variantId' as 'variants', 'variants.products.title' as 'variants'],
    })));
    const latestRegimen: Table.RegimenHistory['attributes'] = JSON.parse(JSON.stringify(await ApiConnector.findOne(Table.RegimenHistory, {
      where: {
        regimenId: this.orderObj.get('regimenId'),
        doctorVerified: true,
      },
      ascending: 'createdAt',
      limit: 1,
      include: ['products'],
      project: ['regimenId', 'products.title' as 'products', 'selectedVariantId'],
    })));
    const changedRegimenProducts = await latestRegimen.products.map((each: any) => each.title);
    const sopRegimenSelectedVariant = sopRegimen.variants
      .find((variant) => variant.variantId === (latestRegimen.selectedVariantId || ApiClientConstant.Regimen.SelectedVariantId.variant_1499));
    console.log('fetchSopQuestions4', sopRegimenSelectedVariant);
    const sopRegimenProducts = (sopRegimenSelectedVariant.products as Array<{ title: string }>).map((each: any) => each.title);
    console.log(changedRegimenProducts, sopRegimenProducts, 'changedRegimenProducts, sopRegimenProducts', 'fetchSopQuestions4')
    changedRegimenProducts.forEach((each: string) => {
      if (!sopRegimenProducts.includes(each)) {
        this.addedProducts.push(each);
      }
    });
    sopRegimenProducts.forEach((each: string) => {
      if (!changedRegimenProducts.includes(each)) {
        this.removedProducts.push(each);
      }
    });
  }

  async subscribeToUrlQueryParams(): Promise<void> {
    this.subscriptions.push(this.route.parent.params.subscribe(() => {
      if (this.route.parent.snapshot.data.order) {
        this.orderObj = this.route.parent.snapshot.data.order;
        this.updateTotalQuantity();
        this.conn.getUserAlternateNumber(this.orderObj.get('user').id)
          .then(([user_]: any) => {
            this.broadcaster.broadcast('ChatUserUpdate', { user: user_ });
            this.faviconService.setFavicon(user_.get('PatientName'));
            this.alternateNumber = user_.get('alternateNumber');
            this.mobileNumber = user_.get('MobileNumber');
          });
        this.fetchPayments();
        this.fetchOrderEditLogs();
        this.fetchPrescriptions();
        if (this.orderObj.get('type') === ApiClientConstant.Order.Type.REGIMEN) this.fetchRegimen();
        this.fetchUnpaidOrders();
        this.checkVariant4999Regimen();
        this.transferPaymentAllowed = [
          ApiClientConstant.Order.Stage.REFUND_REQUESTED,
          ApiClientConstant.Order.Stage.STUCK_IN_TRANSIT,
          ApiClientConstant.Order.Stage.RETURN_INITIATED,
          ApiClientConstant.Order.Stage.RETURNED,
          ApiClientConstant.Order.Stage.LOST_IN_TRANSIT,
          ApiClientConstant.Order.Stage.CANCELED,
        ].includes(this.orderObj.get('stage'));
      }
    }));
    await this.updateOrderHistory();
    this.updateRefundEligibility();
    await this.fetchSopQuestions();
    await this.orderObj.get('sanityChecker')?.fetch();
  }

  async fetchUnpaidOrders(): Promise<any> {
    this.unpaidOrders = await this.conn.getOrders({
      where: { stage: [ApiClientConstant.Order.Stage.ONLINE_PAYMENT_PENDING], user: this.orderObj.get('user') },
      include: ['payment'],
    });
  }

  fetchRegimen(): void {
    this.conn
      .getRegimen(this.orderObj.get('regimenId'))
      .then((regimen: any) => {
        if (!regimen) return;
        this.zone.run(() => {
          this.regimenId = regimen.id;
          this.regimen = regimen;
        });
      });
  }

  async fetchSKUs(): Promise<void> {
    if (!this.orderObj.get('skuEntries')) return;
    const ids = this.orderObj.get('skuEntries').map((entry: any) => entry.id);
    const skus = await this.conn.getSKUEntriesById(ids);
    this.skuIds = skus.map((sku: any) => sku.get('uniqueIdentifier'));
  }

  onlyProductsInOrder(): void {
    if (this.orderObj.get('type') === ApiClientConstant.Order.Type.PRODUCT) {
      const productsForProductOrder: Array<string> = this.orderObj.get('products');
      productsForProductOrder.forEach((product: any) => {
        this.products.push({
          name: product.get('title'),
          MRP: product.get('mrp'),
          SP: product.get('price'),
        });
      });
    } else {
      const productsInfo = this.orderObj.get('productInfo');
      const serviceInfo = this.orderObj.get('serviceInfo');
      const products: Array<string> = this.orderObj.get('products').map((product: any) => product.id);
      const services: Array<string> = this.orderObj.get('services').map((service: any) => service.id);
      this.products = productsInfo.filter((product: any) => products.includes(product.id));
      this.services = serviceInfo.filter((service: any) => services.includes(service.id));
    }
  }

  showToastMessage(message: string): void {
    this.message = message;
    this.toast.nativeElement.style.visibility = 'visible';
    setTimeout(() => {
      this.toast.nativeElement.style.visibility = 'hidden';
    }, 3000);
  }

  fetchPayments(): void {
    this.conn
      .getOrderPayments({ order: this.orderObj })
      .then((payments: any) => this.zone.run(() => this.payments.push(...payments)));
  }

  async fetchOrderEditLogs(): Promise<void> {
    try {
      const response: any = await this.conn.getAuditLogs('Order', this.orderObj.id);
      const logs: Array<any> = response?.data?.data || [];
      const userId: string = logs[0]?.editedBy?.objectId;
      let editedByName: string = '';
      if (userId) {
        const [user]: Array<any> = await ApiConnector.find(Table.User, {
          where: { objectId: userId },
          project: ['DoctorDisplayName', 'PatientName', 'username'],
        });
        if (user) {
          editedByName = user.get('DoctorDisplayName') || user.get('PatientName') || user.get('username');
        }
      }
      this.zone.run(() => {
        this.orderEditLogs = logs;
        this.editedByName = editedByName;
      });
    } catch (error) {
      this.orderEditLogs = [];
    }
  }

  openAddPaymentModal(): void {
    const orderObj = JSON.parse((JSON.stringify(this.orderObj)));
    const dialogRef = this.dialog.open(AddPaymentModal, { data: { order: orderObj } });
    this.subscriptions.push(dialogRef.afterClosed().subscribe((result: any) => {
      this.addPayment(result);
    }));
  }

  addPayment(payment: any): void {
    this.ui.modal.payment.open = false;
    if (!payment) return;
    this.payments.unshift(payment);
  }

  async updateTrackingDetails(): Promise<void> {
    try {
      await this.conn.createQueueEvent(
        this.orderObj.get('user').id,
        'CLICK_POST_TRACKING_UPDATE',
        new Date(),
        { orderId: this.orderObj.id },
        `${this.conn.getCurrentUser().get('username')}_${new Date().getTime()}`);
      this.showToastMessage('Request has been accepted. Check order status after 5min.');
    } catch (error) {
      this.showToastMessage(`ERROR: ${error.message || error}`);
    }
  }

  async processWarehouse(): Promise<void> {
    try {
      await this.conn.createQueueEvent(
        this.orderObj.get('user').id,
        'SELECT_WAREHOUSE_AND_CREATE_ORDER',
        new Date(),
        { orderId: this.orderObj.id },
        `${this.conn.getCurrentUser().get('username')}_${this.orderObj.id}_${moment().format('YYYY-MM-DD')}`);
      this.showToastMessage('Success');
    } catch (error) {
      this.showToastMessage(`ERROR: ${error.message || error}`);
    }
  }

  async assignNewAWB(): Promise<void> {
    try {
      await this.conn.assignNewAWB(this.orderObj.id);
      this.showToastMessage('Success');
    } catch (error) {
      this.showToastMessage(`ERROR: ${error.message || error}`);
    }
  }

  async sendEmiMessage(): Promise<void> {
    try {
      await this.conn.sendEMIMessageToUser(this.orderObj.id);
      this.showToastMessage('Success');
    } catch (error) {
      this.showToastMessage(`ERROR: ${error.message || error}`);
    }
  }

  async initiateRefund(): Promise<void> {
    const labels: Array<string> = this.orderObj.get('labels') || [];
    const isShippedCcod =
      labels.includes('ccodPayment') &&
      this.orderHistory.some((log) => log.get('stage') === ApiClientConstant.Order.Stage.SHIPPED);
    if (
      isShippedCcod &&
      !confirm(
        'This CCOD order has already shipped. Refunds after ship are usually blocked. Confirm to proceed with the refund anyway.',
      )
    ) {
      return;
    }
    try {
      await this.conn.initiateOrderRefund(this.orderObj.id, isShippedCcod);
      this.showToastMessage('Refund initiated successfully');
      this.window.nativeWindow.location.reload();
    } catch (error) {
      this.showToastMessage(`ERROR: ${error.message || error}`);
    }
  }

  addLabelRemovalNote(note: string): void {
    if (!note) {
      return;
    }
    this.note = note;
  }

  generateInvoiceUrl(): void {
    this.conn
      .generateInvoice(this.orderObj.id)
      .then(() => this.showToastMessage('Invoice generated'))
      .catch(() => this.showToastMessage('Unable to generate invoice'));
  }

  generatePrescriptionUrl(): void {
    this.conn.generatePrescriptionForRegimen(this.regimen.id)
      .then(() => this.showToastMessage('Prescription generated'))
      .catch(() => this.showToastMessage('Unable to generate prescription'));
  }

  callUserThroughExotel(number: any): void {
    this.conn.connectCallToUser(number, 'OrderViewPage')
      .then(() => this.showToastMessage('Connecting'))
      .catch(() => this.showToastMessage('Unable to connect to exotel'));
  }

  copyToClipboard(field: any): void {
    this.clipBoardService.copy(String(field));
  }

  async movePayment(): Promise<any> {
    const orderId = this.window.nativeWindow.document.getElementById('transferPayment').value;
    const order = this.unpaidOrders.find((each: any) => each.id === orderId);
    if (!order || !orderId) return alert('no order found');
    const [payment]: any = await this.conn.getOrderPayments({ order: this.orderObj });
    if (!payment) return alert('no payment found');
    const notes = payment.get('notes') ? `${payment.get('notes')}. ` : '';
    payment.set('order', order);
    payment.set('notes', `${notes} Payment transferred from order ${this.orderObj.id}`);
    try {
      await payment.save();
      alert('Payment transferred successfully.');
    } catch (err) {
      alert(err.toString());
    }
    return Promise.resolve();
  }

  activeContactNumber(number: any): void {
    this.orderObj.set('activeContactNumber', number);
    this.orderObj.save();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
    delete this.router;
    delete this.route;
    delete this.appConfig;
    delete this.subscriptions;
    delete this.orderObj;
  }

  private async fetchPrescriptions(): Promise<void> {
    if (this.orderObj.get('prescriptions')?.length) {
      this.prescriptions = await this.conn.findPrescription({
        where: { objectId: this.orderObj.get('prescriptions').map((prescription: any): string => prescription.id) },
        option: { context: { signedURL: true } },
      });
      return;
    }
    if (this.orderObj.get('regimen')) {
      this.prescriptions = await this.conn.fetchAllPrescription(this.orderObj.get('user').id, this.orderObj.get('regimen')?.id);
      return;
    }
    const regimens = await this.conn.findRegimens({ where: { forUser: this.orderObj.get('user'), active: true } });
    this.prescriptions = (await Promise.all(regimens
      .map((each: any) => this.conn.fetchAllPrescription(this.orderObj.get('user').id, each.id)))).flat();
  }

  async setConvertedBy(user: Table.User): Promise<void> {
    try {
      await this.orderObj.save({ convertedBy: user });
      this.broadcaster.broadcast('NOTIFY', {
        message: `Order Converted by ${this.orderObj.get('convertedBy')?.get('username')} Succesfully`,
        type: this.appConfig.Shared.Toast.Type.SUCCESS
      });
      this.showConvertedByButton = false;
    } catch (error) {
      this.broadcaster.broadcast('NOTIFY', { message: error.message, type: this.appConfig.Shared.Toast.Type.ERROR });
    }
  }

  async updateConvertedBy(user: Table.User): Promise<void> {
    if (!(await this.checkPreConditionsForConvertedBy())) {
      this.broadcaster.broadcast('NOTIFY', {
        message: 'The order is either already converted or the duration is over',
        type: this.appConfig.Shared.Toast.Type.ERROR
      })
    } else {
      await this.setConvertedBy(user);
    }
  }

  async checkIfPendingCallExists(): Promise<boolean> {
    const requestPayload: RequestQueryPayload<Table.PendingCall> = {
      where: {
        user: this.orderObj.get('user'),
        status: [
          ApiClientConstant.PendingCall.Status.Requested,
          ApiClientConstant.PendingCall.Status.CallCompleted,
          ApiClientConstant.PendingCall.Status.CallDone,
          ApiClientConstant.PendingCall.Status.OrderPlaced,
          ApiClientConstant.PendingCall.Status.CallReceived,
          ApiClientConstant.PendingCall.Status.OnlinePaymentPending,
        ],
        type: [
          ApiClientConstant.PendingCall.Type.InitialOrder,
          ApiClientConstant.PendingCall.Type.DoctorOrder,
          ApiClientConstant.PendingCall.Type.MissedCall,
          ApiClientConstant.PendingCall.Type.Nutraceutical,
        ],
        createdAt: {
          $gte: moment().subtract(7, 'days').startOf('day').toDate(),
        }
      },
    };

    const pendingCalls = await ApiConnector.count(Table.PendingCall, requestPayload);
    return pendingCalls > 0
  }

  checkIfInitiatedByOperator(): boolean {
    const initialOrderStageLog = this.orderHistory.find(
      (order: Table.OrderConsultationLog) =>
      order.get('stage') === this.apiClientConstant.Order.Stage.INITIAL
    );
    const isInitiatedByOperator = initialOrderStageLog?.get('createdBy')?.get('type') === this.apiClientConstant.User.Type.OPERATOR;
    if (!isInitiatedByOperator) {
      return false;
    }
    return true;
  }

  async checkPreConditionsForConvertedBy(): Promise<boolean> {
    const updatedOrder = await this.conn.getOrderWithSignedURL(this.orderObj.id);
    if (updatedOrder.get('convertedBy')) {
      return false;
    }

    const orderPlacedStageLog = this.orderHistory.find(
      (order: Table.OrderConsultationLog) =>
      order.get('stage') === this.apiClientConstant.Order.Stage.ORDER_PLACED
    );
    const duration = moment.duration(moment().diff(moment(orderPlacedStageLog?.get('createdAt'))));
    if (!orderPlacedStageLog || duration.asMinutes() > 2880) {
      return false;
    }

    const isPlacedByOperator = orderPlacedStageLog.get('createdBy')?.get('type') === this.apiClientConstant.User.Type.OPERATOR;
    if (isPlacedByOperator) {
      await this.setConvertedBy(orderPlacedStageLog.get('createdBy'));
      return false;
    }
    return true;
  }

  async checkConvertedByButtonEligibility(): Promise<boolean> {
    if (!(await this.checkPreConditionsForConvertedBy())) {
      return false;
    }

    const doesPendingCallExists = await this.checkIfPendingCallExists();
    if (doesPendingCallExists) {
      return true;
    }

    return this.checkIfInitiatedByOperator();
  }

  async checkVariant4999Regimen(): Promise<void> {
    const regimens = await this.conn.findRegimens({
      where: { forUser: this.orderObj.get('user'), selectedVariantId: 'variant_4999' },
      project: ['selectedVariantId'],
    });
    this.hasVariant4999Regimen = regimens.length > 0;
  }

  async toggleUserMobileNumberVisibility(): Promise<void> {
    this.showUserMobileNumber = !this.showUserMobileNumber;
    const result = await this.conn.decryptTable({ objectId: this.orderObj.id, userId: this.orderObj.get('user').id, tableName: 'Order', fieldNames: ['contactNumber']});
    this.orderObj.set('contactNumber', result.contactNumber);
  }

  protected readonly apiClientConstant: typeof ApiClientConstant = ApiClientConstant;
}
